import './Form.scss'
import { useForm } from 'react-hook-form'
import { Field, Select, Switch } from '@fluentui/react-components'
import { Button } from '../Button'
import { toast } from 'react-toastify'
import { createRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { Loading } from '../Loading'
import { ErrorComponent } from '../ErrorComponent'
import { useState } from 'react'
import { OverlayDrawerComponent } from '../OverlayDrawerComponent/OverlayDrawerComponent'

type MessagesApplicationsApplicationRole = RestSchemas['Messages.Applications.ApplicationRole']
type MessagesApplicationsNewDataAssetPermission = RestSchemas['Messages.Applications.NewDataAssetPermission']
type MessagesApplicationsNewFileTemplateRole = RestSchemas['Messages.Applications.NewFileTemplateRole']
type MessagesDataAssetsDataAsset = RestSchemas['Messages.DataAssets.DataAsset']
type MessagesTemplatesFileTemplate = RestSchemas['Messages.Templates.FileTemplate']
type MessagesApplicationsRoleTemplateAssetPermissions =
  RestSchemas['Messages.Applications.RoleTemplateAssetPermissions']

type ApplicationPermissionsFormProps = {
  onSubmit: () => void
  onDelete?: () => void // Optional delete callback
  applicationId: number
  appRoles: MessagesApplicationsApplicationRole[]
  dataAssets: MessagesDataAssetsDataAsset[]
  fileTemplates: MessagesTemplatesFileTemplate[]
  edit?: boolean
  delete?: boolean
  selectedAppPerm?: MessagesApplicationsRoleTemplateAssetPermissions
  unavailableOptions?: { roleId: number; ftOrDaKey: string }[]
}

export const ApplicationPermissionsForm = (
  props: Omit<ApplicationPermissionsFormProps, 'appRoles' | 'dataAssets' | 'fileTemplates'>
) => {
  const { getApplicationsApiListApplicationRoles, getDataAssetApiListDataAssets, getTemplatesApiListFileTemplates } =
    createRestClient()

  const {
    data: fileTemplates,
    isPending: templatesLoading,
    isError: templatesError,
  } = getTemplatesApiListFileTemplates.useQuery({
    query: {
      applicationId: props.applicationId,
    },
  })

  const {
    data: dataAssets,
    isPending: dataAssetsLoading,
    isError: dataAssetsError,
  } = getDataAssetApiListDataAssets.useQuery({
    query: {
      applicationId: props.applicationId,
    },
  })

  const {
    data: appRoles,
    isPending: rolesLoading,
    isError: rolesError,
  } = getApplicationsApiListApplicationRoles.useQuery({
    query: {
      applicationId: props.applicationId,
    },
  })

  if (dataAssetsLoading || rolesLoading || templatesLoading) return <Loading />
  if (dataAssetsError || rolesError || templatesError) return <ErrorComponent />

  return <Form {...props} dataAssets={dataAssets} appRoles={appRoles} fileTemplates={fileTemplates} />
}

type ApplicationPermissionsForm = MessagesApplicationsNewDataAssetPermission &
  MessagesApplicationsNewFileTemplateRole & { fileTemplateIdOrDataAssetId: string }

function Form(props: Omit<ApplicationPermissionsFormProps, 'applicationId' | 'entityTypeId'>) {
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)

  const allowed = props.selectedAppPerm ? JSON.parse(props.selectedAppPerm!.permissions!)[0] : []

  const {
    register,
    formState: { errors },
    reset,
    handleSubmit,
    watch,
  } = useForm<Required<ApplicationPermissionsForm>>({
    defaultValues:
      props.edit || props.delete
        ? {
            fileTemplateIdOrDataAssetId:
              props.selectedAppPerm!.resource == 'assets'
                ? `da_${props.selectedAppPerm!.id!}`
                : `ft_${props.selectedAppPerm!.id!}`,
            roleId: props.selectedAppPerm?.roleId,

            allowRead: allowed.allow_read,
            allowUpload: allowed.allow_upload,
            allowDownloadDraft: allowed.allow_download_draft,
            allowComment: allowed.allow_comment,
            allowAccept: allowed.allow_accept,
            allowDiscard: allowed.allow_discard,
          }
        : {
            allowRead: true,
            allowUpload: false,
            allowDownloadDraft: false,
            allowComment: true,
            allowAccept: false,
            allowDiscard: true,
          },
  })

  const {
    putApplicationsApiAddDataAssetPermission,
    postApplicationsApiUpdateDataAssetPermission,
    deleteApplicationsApiDeleteDataAssetPermission,
    putApplicationsApiAddFileTemplateRole,
    postApplicationsApiUpdateFileTemplateRole,
    deleteApplicationsApiDeleteFileTemplateRole,
  } = createRestClient()

  const {
    mutate: putNewDataAssetPermission,
    // isPending: putNewDataAssetPermissionLoading,
    // isError: putNewDataAssetPermissionError,
  } = putApplicationsApiAddDataAssetPermission.useMutation()

  const {
    mutate: putNewFileTemplatePermission,
    // isPending: putNewNewFileTemplatePermissionLoading,
    // isError: putNewNewFileTemplatePermissionError,
  } = putApplicationsApiAddFileTemplateRole.useMutation()

  const {
    mutate: updateDataAssetPermission,
    // isPending: putNewDataAssetPermissionLoading,
    // isError: putNewDataAssetPermissionError,
  } = postApplicationsApiUpdateDataAssetPermission.useMutation()

  const {
    mutate: updateFileTemplatePermission,
    // isPending: putNewNewFileTemplatePermissionLoading,
    // isError: putNewNewFileTemplatePermissionError,
  } = postApplicationsApiUpdateFileTemplateRole.useMutation()

  const onSubmit = (data: Required<ApplicationPermissionsForm>) => {
    let roleType: 'da' | 'ft' = 'da'
    if (data.fileTemplateIdOrDataAssetId.startsWith('da_')) {
      roleType = 'da'
      data.dataAssetId = parseInt(data.fileTemplateIdOrDataAssetId.substring(3))
      data.fileTemplateId = 0
    } else if (data.fileTemplateIdOrDataAssetId.startsWith('ft_')) {
      roleType = 'ft'
      data.dataAssetId = 0
      data.fileTemplateId = parseInt(data.fileTemplateIdOrDataAssetId.substring(3))
    } else {
      if (!data.roleId || (data.roleId as unknown) == 'None')
        toast.warning('Missing role and data asset / file template')
      else toast.warning('Missing data asset / file template')
      return false
    }

    if (!data.roleId || (data.roleId as unknown) == 'None') {
      toast.warning('Missing role')
      return false
    }

    if (
      props.unavailableOptions &&
      props.unavailableOptions.some((x) => x.roleId == data.roleId && x.ftOrDaKey == data.fileTemplateIdOrDataAssetId)
    ) {
      toast.warning('Already a config for selected Role and Data Asset / File Template. Edit existing config instead.')
      return false
    }

    if (props.edit) {
      if (roleType == 'da') {
        const newDaPerm: MessagesApplicationsNewDataAssetPermission = {
          ...data,
        }

        updateDataAssetPermission(
          {
            body: newDaPerm,
          },
          {
            onSuccess() {
              toast.success('Updated data asset permission')
              props.onSubmit()
              reset()
            },
            onError() {
              toast.error(`Error updating data asset permission`)
            },
          }
        )
      } else {
        const newFtPerm: MessagesApplicationsNewFileTemplateRole = {
          ...data,
        }

        updateFileTemplatePermission(
          {
            body: newFtPerm,
          },
          {
            onSuccess() {
              toast.success('Updated file template permission')
              props.onSubmit()
              reset()
            },
            onError() {
              toast.error(`Error updating file template permission`)
            },
          }
        )
      }
    } else {
      if (roleType == 'da') {
        const newDaPerm: MessagesApplicationsNewDataAssetPermission = {
          ...data,
        }

        putNewDataAssetPermission(
          {
            body: newDaPerm,
          },
          {
            onSuccess() {
              toast.success('New data asset permission added')
              props.onSubmit()
              reset()
            },
            onError() {
              toast.error(`Error adding data asset permission`)
            },
          }
        )
      } else {
        const newFtPerm: MessagesApplicationsNewFileTemplateRole = {
          ...data,
        }

        putNewFileTemplatePermission(
          {
            body: newFtPerm,
          },
          {
            onSuccess() {
              toast.success('New file template permission added')
              props.onSubmit()
              reset()
            },
            onError() {
              toast.error(`Error adding file template permission`)
            },
          }
        )
      }
    }
  }

  const handleDelete = async () => {
    if (props.selectedAppPerm!.resource == 'assets') {
      const {
        mutate: deleteDataAssetPermission,
        // isPending: putNewNewFileTemplatePermissionLoading,
        // isError: putNewNewFileTemplatePermissionError,
      } = deleteApplicationsApiDeleteDataAssetPermission.useMutation()

      deleteDataAssetPermission(
        {
          query: { roleId: props.selectedAppPerm!.roleId!, dataAssetId: props.selectedAppPerm!.id },
        },
        {
          onSuccess() {
            toast.success('Deleted data asset permission')
            setIsDeleteConfirmOpen(false) // Close the drawer after toast displays
            props.onDelete!() // Ensure this does not cause the parent component to unmount immediately
          },
          onError() {
            toast.error(`Error deleting data asset permission`)
          },
        }
      )
    } else {
      const {
        mutate: deleteFileTemplatePermission,
        // isPending: putNewNewFileTemplatePermissionLoading,
        // isError: putNewNewFileTemplatePermissionError,
      } = deleteApplicationsApiDeleteFileTemplateRole.useMutation()

      deleteFileTemplatePermission(
        {
          query: { roleId: props.selectedAppPerm!.roleId!, fileTemplateId: props.selectedAppPerm!.id },
        },
        {
          onSuccess() {
            toast.success('Deleted file template permission')
            setIsDeleteConfirmOpen(false) // Close the drawer after toast displays
            props.onDelete!() // Ensure this does not cause the parent component to unmount immediately
          },
          onError() {
            toast.error(`Error deleting file template permission`)
          },
        }
      )
    }
  }

  const watchFileTemplateIdOrDataAssetId = watch('fileTemplateIdOrDataAssetId')
  const watchRoleId = watch('roleId')

  const booleanFields =
    watchFileTemplateIdOrDataAssetId === undefined || watchFileTemplateIdOrDataAssetId === 'None'
      ? []
      : [
          { name: 'allowRead', label: 'Allow Read' },
          { name: 'allowUpload', label: 'Allow Upload' },
          ...(watchFileTemplateIdOrDataAssetId?.startsWith('da_')
            ? [{ name: 'allowDownloadDraft', label: 'Allow Download Draft' }]
            : []),
          { name: 'allowDelete', label: 'Allow Delete' },
          { name: 'allowComment', label: 'Allow Comment' },
          { name: 'allowAccept', label: 'Allow Accept' },
          { name: 'allowDiscard', label: 'Allow Discard' },
        ]

  return (
    <div className="form-wrapper">
      <div>
        <Field
          label="Role"
          required
          validationState={errors.roleId ? 'error' : 'none'}
          validationMessage={errors.roleId?.message}
        >
          <Select
            disabled={props.edit || props.delete}
            {...register('roleId', {
              maxLength: 256,
            })}
          >
            <option key={'none'} value={undefined}>
              None
            </option>
            {props.appRoles.map((p) => {
              return (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              )
            })}
          </Select>
        </Field>

        <Field
          label="File Template / Data Asset"
          required
          validationState={errors.fileTemplateIdOrDataAssetId || errors.fileTemplateIdOrDataAssetId ? 'error' : 'none'}
          validationMessage={errors.fileTemplateIdOrDataAssetId?.message || errors.fileTemplateIdOrDataAssetId?.message}
        >
          <Select
            disabled={props.edit || props.delete}
            {...register('fileTemplateIdOrDataAssetId', {
              maxLength: 256,
            })}
          >
            <option key={'none'} value={undefined}>
              None
            </option>
            {props.dataAssets.map((p) => {
              return (
                <option
                  key={`da_${p.id}`}
                  value={`da_${p.id}`}
                  disabled={
                    props.unavailableOptions &&
                    props.unavailableOptions.some((x) => x.roleId == watchRoleId && x.ftOrDaKey == `da_${p.id}`)
                  }
                >
                  Data Asset: {p.name}
                </option>
              )
            })}

            {props.fileTemplates.map((p) => {
              return (
                <option
                  key={`ft_${p.id}`}
                  value={`ft_${p.id}`}
                  disabled={
                    !!props.unavailableOptions &&
                    props.unavailableOptions.some((x) => x.roleId == watchRoleId && x.ftOrDaKey == `ft_${p.id}`)
                  }
                >
                  File Template: {p.name}
                </option>
              )
            })}
          </Select>
        </Field>

        {booleanFields.map((field) => (
          <Field
            key={field.name}
            label={field.label}
            validationState={errors[field.name as keyof ApplicationPermissionsForm] ? 'error' : 'none'}
            validationMessage={errors[field.name as keyof ApplicationPermissionsForm]?.message}
          >
            <Switch {...register(field.name as keyof ApplicationPermissionsForm)} disabled={props.delete} />
          </Field>
        ))}
      </div>
      <div className="form-action-container">
        {props.delete && props.onDelete && <Button text="Delete" onClick={() => setIsDeleteConfirmOpen(true)} />}
        {!props.delete && <Button text="Submit" onClick={handleSubmit(onSubmit)} />}
      </div>

      {/* Confirmation Modal for Delete */}
      <OverlayDrawerComponent
        open={isDeleteConfirmOpen}
        setOpen={setIsDeleteConfirmOpen}
        title="Confirm Delete"
        content={
          <div>
            <p>Are you sure you want to delete this set of permissions?</p>
            <Button text="Confirm" onClick={handleDelete} />
          </div>
        }
      />
    </div>
  )
}
